import { AlertCircle } from '@aktiia/design-system';
import cx from 'classnames';
import { useMemo } from 'react';

import { NumberInput, PasswordInput, SearchInput, SelectInput, TextAreaInput, TextInput } from './components';
import { IInputProps } from './types';

export const Input = ({
  value,
  label,
  helpLabel,
  maxLength,
  minValue,
  maxValue,
  options = {},
  autoComplete = 'new-password',
  onChange,
  placeholder,
  leftIcon,
  rightIcon,
  type = 'text',
  className,
  hasError,
  errorLabel,
}: IInputProps) => {
  const alertLabel = useMemo(
    () => (hasError && errorLabel ? errorLabel : helpLabel),
    [hasError, errorLabel, helpLabel],
  );
  const classNames = cx(
    'w-full px-4 outline-none rounded-lg',
    'typography-other-input text-text-primary placeholder:text-text-quartenary',
    'border border-solid border-border-medium focus:border-border-strong',
    {
      'border-border-strong': value?.length,
    },
    {
      'border-border-negative': hasError,
    },
  );

  const getInput = () => {
    switch (type) {
      case 'number':
        return (
          <NumberInput
            onChange={(value) => onChange(`${value}`)}
            value={+(value ?? 0)}
            className={classNames}
            placeholder={placeholder}
            minValue={minValue}
            maxValue={maxValue}
          />
        );
      case 'password':
        return (
          <PasswordInput
            onChange={onChange}
            value={value}
            className={classNames}
            placeholder={placeholder}
            autoComplete={autoComplete}
          />
        );
      case 'search':
        return <SearchInput onChange={onChange} value={value} className={classNames} placeholder={placeholder} />;
      case 'select':
        return (
          <SelectInput
            onChange={onChange}
            value={value}
            options={options}
            className={classNames}
            placeholder={placeholder}
          />
        );
      case 'textarea':
        return <TextAreaInput onChange={onChange} value={value} className={classNames} placeholder={placeholder} />;
      default:
        return (
          <TextInput
            onChange={onChange}
            value={value}
            className={classNames}
            placeholder={placeholder}
            maxLength={maxLength}
            leftIcon={leftIcon}
            rightIcon={rightIcon}
            autoComplete={autoComplete}
          />
        );
    }
  };

  return (
    <div className={cx('flex flex-col', className)}>
      {label && (
        <div className="flex mb-2">
          <span className="typography-title-3 text-text-primary">{label}</span>
          <div className="grow" />
          {maxLength && (
            <span className="text-title-3 text-text-tertiary">
              {value?.length ?? 0}/{maxLength}
            </span>
          )}
        </div>
      )}
      {getInput()}
      {alertLabel && (
        <div className="flex items-center mt-2">
          <span className={cx({ 'text-icon-negative': hasError }, { 'text-icon-tertiary': !hasError })}>
            <AlertCircle className="scale-50" />
          </span>
          <span
            className={cx(
              'typography-paragraph-3',
              { 'text-text-negative': hasError },
              { 'text-text-tertiary': !hasError },
            )}
          >
            {alertLabel}
          </span>
        </div>
      )}
    </div>
  );
};
